// ============================================
// MONUMENT VALLEY INSPIRED COLOR PALETTES
// ============================================

// Multiple palettes like the previous project
export const MONUMENT_PALETTES = {
  // Warm sunset palette (default for real world)
  sunset: {
    sky: '#ffecd2',
    ground: '#fcb69f',
    primary: '#ff9a9e',
    secondary: '#fad0c4',
    accent: '#ffecd2',
    shadow: '#d4a5a5',
    stone: '#e8c4b8',
    highlight: '#fff5ee',
  },
  // Cool ocean palette
  ocean: {
    sky: '#e0f7fa',
    ground: '#80deea',
    primary: '#4dd0e1',
    secondary: '#b2ebf2',
    accent: '#ffffff',
    shadow: '#78909c',
    stone: '#90a4ae',
    highlight: '#e0f7fa',
  },
  // Forest green palette
  forest: {
    sky: '#e8f5e9',
    ground: '#a5d6a7',
    primary: '#81c784',
    secondary: '#c8e6c9',
    accent: '#ffffff',
    shadow: '#689f38',
    stone: '#aed581',
    highlight: '#f1f8e9',
  },
  // Mystical purple palette (for spectral world)
  mystical: {
    sky: '#1a1a2e',
    ground: '#16213e',
    primary: '#9575cd',
    secondary: '#7986cb',
    accent: '#c5cae9',
    shadow: '#283593',
    stone: '#5c6bc0',
    highlight: '#e8eaf6',
    glow: '#b39ddb',
    ethereal: '#7c4dff',
  },
  // Desert sand palette
  desert: {
    sky: '#fff8e1',
    ground: '#ffe082',
    primary: '#ffca28',
    secondary: '#fff59d',
    accent: '#ffffff',
    shadow: '#ffa000',
    stone: '#ffcc80',
    highlight: '#fffde7',
  },
  // Night blue palette (for spectral world)
  night: {
    sky: '#0d1b2a',
    ground: '#1b263b',
    primary: '#415a77',
    secondary: '#778da9',
    accent: '#e0e1dd',
    shadow: '#0d1b2a',
    stone: '#415a77',
    highlight: '#e0e1dd',
    glow: '#7fb3d5',
    ethereal: '#85c1e9',
  },
} as const;

export type PaletteName = keyof typeof MONUMENT_PALETTES;

// Simplified access for real/spectral worlds
export const COLORS = {
  // Real World - Uses sunset palette
  real: {
    ...MONUMENT_PALETTES.sunset,
    wood: '#a67c52',
    gold: '#e6c47f',
    green: '#8fa887',
  },

  // Spectral World - Uses night palette
  spectral: {
    ...MONUMENT_PALETTES.night,
    void: '#0d1b2a',
  },

  // UI Colors
  ui: {
    text: '#2C3E50',
    textLight: '#F8F9FA',
    success: '#7DCEA0',
    warning: '#F4D03F',
    danger: '#E74C3C',
    disabled: '#BDC3C7',
  },
} as const;

// ============================================
// LIGHTING PRESETS (Monument Valley Style)
// ============================================

export const LIGHTING = {
  real: {
    // Hemispheric-style lighting (top + ground color)
    ambient: {
      color: '#ffecd2',      // Warm from sky
      groundColor: '#d4a5a5', // Shadow tint from ground
      intensity: 0.8,
    },
    directional: {
      color: '#ffecd2',
      intensity: 0.4,        // Subtle - not harsh shadows
      position: [-5, 10, -5] as [number, number, number],
    },
    fog: {
      color: '#ffecd2',
      near: 15,
      far: 40,
    },
  },
  spectral: {
    ambient: {
      color: '#4a5a77',
      groundColor: '#1a2a3e',
      intensity: 0.6,
    },
    directional: {
      color: '#9fc3e5',
      intensity: 0.4,
      position: [-3, 8, -3] as [number, number, number],
    },
    fog: {
      color: '#0d1b2a',
      near: 20,  // Push fog further away
      far: 60,   // Extend fog range
    },
  },
} as const;

// Palette-specific lighting presets
export const PALETTE_LIGHTING = {
  sunset: LIGHTING.real,
  forest: {
    ambient: {
      color: '#e8f5e9',
      groundColor: '#689f38',
      intensity: 0.75,
    },
    directional: {
      color: '#c8e6c9',
      intensity: 0.5,
      position: [-4, 12, -4] as [number, number, number],
    },
    fog: {
      color: '#e8f5e9',
      near: 18,
      far: 45,
    },
  },
  ocean: {
    ambient: {
      color: '#e0f7fa',
      groundColor: '#78909c',
      intensity: 0.8,
    },
    directional: {
      color: '#b2ebf2',
      intensity: 0.45,
      position: [-5, 10, -5] as [number, number, number],
    },
    fog: {
      color: '#e0f7fa',
      near: 15,
      far: 40,
    },
  },
  desert: {
    ambient: {
      color: '#fff8e1',
      groundColor: '#ffa000',
      intensity: 0.85,
    },
    directional: {
      color: '#ffe082',
      intensity: 0.5,
      position: [-4, 14, -4] as [number, number, number],
    },
    fog: {
      color: '#fff8e1',
      near: 20,
      far: 50,
    },
  },
  mystical: LIGHTING.spectral,
  night: LIGHTING.spectral,
} as const;

// ============================================
// MATERIAL SETTINGS (Flat Shading)
// ============================================

export const MATERIALS = {
  // Flat shading settings - no specular, slight emissive
  flat: {
    roughness: 1,          // Completely matte
    metalness: 0,          // No metallic reflection
    emissiveIntensity: 0.15, // Slight self-illumination for flat look
    flatShading: true,     // Low-poly geometric look
  },
  // For glowing objects (crystals, magic)
  glow: {
    roughness: 0.3,
    metalness: 0.2,
    emissiveIntensity: 0.6,
    flatShading: false,
  },
  // For ethereal/ghostly objects
  ethereal: {
    roughness: 0.5,
    metalness: 0,
    emissiveIntensity: 0.4,
    transparent: true,
    opacity: 0.7,
  },
} as const;

// ============================================
// CAMERA SETTINGS (Isometric)
// ============================================

export const CAMERA = {
  // True isometric angle
  isometricAngle: Math.atan(Math.sin(Math.PI / 4)), // ~35.264°
  distance: 20,
  fov: 50,
  near: 0.1,
  far: 1000,
  // Orthographic settings for true isometric
  orthographic: {
    zoom: 50,
  },
} as const;

// ============================================
// GAME SETTINGS
// ============================================

export const GAME = {
  tileSize: 1,
  playerMoveSpeed: 4,
  worldTransitionDuration: 800, // ms
  interactionDistance: 1.5,
  defaultRoomSize: [10, 8, 10] as [number, number, number],
} as const;

// ============================================
// INPUT SETTINGS
// ============================================

export const INPUT = {
  keyboard: {
    up: ['KeyW', 'ArrowUp'],
    down: ['KeyS', 'ArrowDown'],
    left: ['KeyA', 'ArrowLeft'],
    right: ['KeyD', 'ArrowRight'],
    interact: ['KeyE'],
    switchWorld: ['Space', 'KeyQ'],
    pause: ['Escape'],
  },
  touch: {
    joystickDeadzone: 0.1,
    doubleTapThreshold: 300, // ms
    longPressThreshold: 500, // ms
  },
} as const;

// ============================================
// Z-INDEX / RENDER ORDER
// ============================================

export const RENDER_ORDER = {
  floor: 0,
  walls: 1,
  objects: 2,
  characters: 3,
  effects: 4,
  ui: 5,
} as const;
